/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Stack,
    useStyleConfig,
    Box,
    Text,
    Heading,
    Link,
    Button
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {FormattedMessage} from 'react-intl'
import useBackInStock from '../../forms/useBackInStock'
import Field from '../../field'
import {
    BIRTHDAY_MONTHS,
    KS_PERSONAL_PRIVACY_POLICY_URL,
    KS_PERSONAL_TERMS_CONDITIONS_URL
} from '../../../constants'

const ProductNotifyForm = ({product}) => {
    const {id} = product
    const form = useForm()
    const fields = useBackInStock({form})
    const {data: customer} = useCurrentCustomer()
    const [showSuccessMessage, setShowSuccessMessage] = useState()
    const styles = useStyleConfig('FormStyles')
    const {
        app: {commerceAPI}
    } = getConfig()
    const {hostUrl} = commerceAPI.storefront

    useEffect(() => {
        const formValues = form.getValues()
        const {firstName, lastName, email, birthday} = formValues
        if (customer?.customerType === 'registered' && customer?.email) {
            if (!firstName && customer?.firstName) {
                form.setValue('firstName', customer?.firstName)
            }
            if (!lastName && customer?.lastName) {
                form.setValue('lastName', customer?.lastName)
            }
            if (!email && customer?.email) {
                form.setValue('email', customer?.email)
            }
            if (birthday === 'Make a selection' && customer?.c_birthdayMonth) {
                form.setValue(
                    'birthday',
                    BIRTHDAY_MONTHS?.find(({id}) => id === customer?.c_birthdayMonth)?.value
                )
            }
        }
    }, [customer?.customerType, customer?.email])

    const submitWaitlist = () => {
        const formValues = form.getValues()
        const {firstName, lastName, email, birthday} = formValues

        if (product?.c_comingSoonProduct) {
            window._ltk.Signup.New('ComingSoon', `CS_email_${id}`, window._ltk.Signup.TYPE.CUSTOM)
            window._ltk.Signup.SetValue('ComingSoon', 'cs_firstname', firstName)
            window._ltk.Signup.SetValue('ComingSoon', 'cs_lastname', lastName)
            window._ltk.Signup.SetValue('ComingSoon', 'cs_bday', birthday)
            window._ltk.Signup.SetValue('ComingSoon', 'ltkSaved', true)
            window._ltk.Signup.Subscribe('ComingSoon')
            window._ltk.Alerts.AddAlert(email, id, 'CS')
        } else {
            window._ltk.Signup.New('BackInStock', `bis_email_${id}`, window._ltk.Signup.TYPE.CUSTOM)
            window._ltk.Signup.SetValue('BackInStock', 'bis_firstname', firstName)
            window._ltk.Signup.SetValue('BackInStock', 'bis_lastname', lastName)
            window._ltk.Signup.SetValue('BackInStock', 'bis_bday', birthday)
            window._ltk.Signup.SetValue('BackInStock', 'ltkSaved', true)
            window._ltk.Signup.Subscribe('BackInStock')
            window._ltk.Alerts.AddAlert(email, id, 'PA')
        }
        window._ltk.Alerts.Submit()
        setShowSuccessMessage(true)
    }

    useEffect(() => {
        setShowSuccessMessage(false)
    }, [product?.id])
    return (
        <Box>
            {showSuccessMessage && (
                <Box>
                    <Heading
                        fontSize="23px"
                        fontWeight="400"
                        marginBottom="10px"
                        letterSpacing="0.115px"
                        lineHeight="normal"
                        color="black.100"
                    >
                        <FormattedMessage id="done" defaultMessage="Done!" />
                    </Heading>
                    <Text
                        fontSize="16px"
                        fontWeight="300"
                        letterSpacing="0.08px"
                        lineHeight="25px"
                        marginBottom="30px"
                        color="black.100"
                    >
                        <FormattedMessage
                            id="done.message"
                            defaultMessage="You'll be the first to know when this is available."
                        />
                    </Text>
                </Box>
            )}
            {!showSuccessMessage && (
                <form
                    id="product-notify-form"
                    name="product-notify-form"
                    onSubmit={form.handleSubmit(submitWaitlist)}
                    data-testid="sf-product-notify-form"
                >
                    <Box>
                        <Heading
                            fontSize="23px"
                            fontWeight="400"
                            marginBottom="10px"
                            letterSpacing="0.115px"
                            lineHeight="normal"
                            color="black.100"
                        >
                            {!product?.c_comingSoonProduct ? (
                                <FormattedMessage id="sold.out" defaultMessage="Sold Out" />
                            ) : (
                                (product?.c_comingSoonProduct &&
                                    product?.c_comingSoonFormTitle) || (
                                    <FormattedMessage
                                        id="back.in.stock.heading"
                                        defaultMessage="Shop it first!"
                                    />
                                )
                            )}
                        </Heading>
                        <Text
                            fontSize="16px"
                            fontWeight="300"
                            letterSpacing="0.08px"
                            lineHeight="25px"
                            marginBottom="30px"
                            color="black.100"
                        >
                            {product?.c_comingSoonProduct && product?.c_comingSoonFormDesc ? (
                                product?.c_comingSoonFormDesc
                            ) : (
                                <FormattedMessage
                                    id="back.in.stock.description"
                                    defaultMessage="Sign up to our mailing list and be notified when this style becomes available."
                                />
                            )}
                        </Text>
                    </Box>
                    <Stack {...styles.fieldsStack}>
                        <Field
                            {...fields.firstName}
                            inputId={
                                product?.c_comingSoonProduct
                                    ? `CS_firstname_${id}`
                                    : `bis_firstname_${id}`
                            }
                            inputName={
                                product?.c_comingSoonProduct ? 'CS_firstname' : 'bis_firstname'
                            }
                        />
                        <Field
                            {...fields.lastName}
                            inputId={
                                product?.c_comingSoonProduct
                                    ? `CS_lastname_${id}`
                                    : `bis_lastname_${id}`
                            }
                            inputName={
                                product?.c_comingSoonProduct ? 'CS_lastname' : 'bis_lastname'
                            }
                        />
                        <Field
                            {...fields.email}
                            inputId={
                                product?.c_comingSoonProduct ? `CS_email_${id}` : `bis_email_${id}`
                            }
                            inputName={product?.c_comingSoonProduct ? 'CS_email' : 'bis_email'}
                        />
                        <Field
                            {...fields.birthday}
                            inputId={
                                product?.c_comingSoonProduct ? `CS_bday_${id}` : `bis_bday_${id}`
                            }
                            inputName={product?.c_comingSoonProduct ? 'CS_bday' : 'bis_bday'}
                        />
                    </Stack>
                    <Text
                        marginTop="20px"
                        color="black.100"
                        fontSize="13px"
                        textUnderlineOffset="4px"
                        lineHeight="22px"
                        fontWeight="300"
                        letterSpacing="0.065px"
                    >
                        {product?.c_comingSoonProduct ? (
                            <FormattedMessage
                                id="join.the.wait.list.join_the_waitlist"
                                defaultMessage="By clicking Join the Waitlist, you agree to Kendra Scott’s "
                            />
                        ) : (
                            <FormattedMessage
                                id="join.the.wait.list.agreement_notify_me"
                                defaultMessage="By clicking Notify Me, you agree to Kendra Scott’s "
                            />
                        )}
                        <Text as="span" display="block">
                            <Link
                                href={hostUrl + KS_PERSONAL_TERMS_CONDITIONS_URL}
                                variant="underline"
                            >
                                <FormattedMessage
                                    id="join.the.wait.list.terms_conditions"
                                    defaultMessage="Terms & Conditions"
                                />
                            </Link>{' '}
                            <FormattedMessage id="join.the.wait.list.and" defaultMessage="and" />{' '}
                            <Link
                                href={hostUrl + KS_PERSONAL_PRIVACY_POLICY_URL}
                                variant="underline"
                            >
                                <FormattedMessage
                                    id="join.the.wait.list.privacy_policy"
                                    defaultMessage="Privacy Policy"
                                />
                            </Link>
                            {'.'}
                        </Text>
                    </Text>
                    <Button
                        type="submit"
                        backgroundColor="gray.450"
                        marginTop="30px"
                        _hover={{
                            backgroundColor: 'gray.400',
                            color: 'black.100'
                        }}
                        border="none"
                        width="100%"
                        height="56px"
                        color="black.100"
                        lineHeight="26px"
                        fontSize="17px"
                        fontWeight="400"
                        borderRadius="90px"
                    >
                        {!product?.c_comingSoonProduct ? (
                            <FormattedMessage id="notify.me" defaultMessage="Notify Me" />
                        ) : (
                            <FormattedMessage
                                id="join.the.waitlist"
                                defaultMessage="Join the Waitlist"
                            />
                        )}
                    </Button>
                </form>
            )}
        </Box>
    )
}

ProductNotifyForm.propTypes = {
    product: PropTypes.object
}

export default ProductNotifyForm
